import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { saleBlock } from "@/lib/saleStatus";
import axios from "axios";
import Navbar from "@/components/Navbar";
import AuthModal from "@/components/AuthModal";
import { ArrowRight, Bot, Columns3, Headphones, Package, RefreshCw, ShieldCheck, Sparkles, Star, Zap } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";

const PRODUCT_DEFAULTS = {
  gestion: {
    description: "Gérez votre serveur Discord avec des outils de modération, de tickets, de rôles et de statistiques.",
    logo: "/images/logo-gestion.svg",
    banner: "/images/banner-gestion.svg",
  },
  radio: {
    description: "Animez votre serveur avec une radio Discord fluide, des playlists et une diffusion musicale continue.",
    logo: "/images/logo-radio.svg",
    banner: "/images/banner-radio.svg",
  },
};

// Offre la moins chère d'un produit (pour « À partir de »).
const cheapestPlan = (bot) => (bot.plans || [])
  .filter((p) => Number.isFinite(Number(p.price)) && Number(p.price) > 0)
  .sort((a, b) => Number(a.price) - Number(b.price))[0] || null;
const euros = (n) => `${Number.isInteger(n) ? n : n.toFixed(2).replace(".", ",")} €`;

const FILTERS = [
  { id: "all", label: "Tout" },
  { id: "discord", label: "Bots Discord" },
  { id: "fivem", label: "Scripts FiveM" },
];

const PERKS = [
  { icon: ShieldCheck, title: "Paiement sécurisé", text: "Carte, PayPal et plus" },
  { icon: Zap, title: "Accès immédiat", text: "Dès le paiement validé" },
  { icon: RefreshCw, title: "Mises à jour", text: "Incluses avec ta licence" },
  { icon: Headphones, title: "Support", text: "Assistant IA et tickets" },
];

function ShopCard({ bot, index, onOpen }) {
  const banner = bot.banner_url || PRODUCT_DEFAULTS[bot.id]?.banner;
  const logo = bot.image_url || PRODUCT_DEFAULTS[bot.id]?.logo;
  const plan = cheapestPlan(bot);
  const color = bot.color || "#3b82f6";
  const block = saleBlock(bot);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`shop-card-${bot.id}`}
      className="shop-card group relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0f1019] text-left transition-all duration-300 hover:-translate-y-1 hover:border-white/15 hover:shadow-[0_24px_60px_-24px_var(--glow)]"
      style={{ "--glow": `${color}99`, animationDelay: `${index * 70}ms` }}
    >
      <div className="relative h-44 overflow-hidden">
        {banner ? (
          <img src={banner} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center" style={{ background: `radial-gradient(120% 120% at 0% 0%, ${color}55, transparent 60%), radial-gradient(100% 100% at 100% 100%, ${color}33, transparent 60%), #0b0c14` }}>
            <Bot className="h-14 w-14 opacity-60" style={{ color }} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1019] via-[#0f1019]/10 to-transparent" />
        <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/45 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-md">
          {bot.downloadable ? <Package className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
          {bot.downloadable ? "Script FiveM" : "Bot Discord"}
        </span>
        {bot.trial_days > 0 && (
          <span className="absolute right-4 top-4 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg shadow-emerald-900/40">
            Essai {bot.trial_days} j
          </span>
        )}
      </div>

      <div className="relative -mt-8 flex flex-1 flex-col px-5 pb-5">
        <div className="flex items-end gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-[#0f1019] shadow-xl" style={{ boxShadow: `0 10px 30px -10px ${color}88` }}>
            {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : <Bot className="h-7 w-7" style={{ color }} />}
          </div>
          <div className="min-w-0 pb-0.5">
            <h2 className="truncate font-heading text-[17px] font-bold leading-tight">{bot.name}</h2>
            {block
              ? <span data-testid={`shop-sale-blocked-${bot.id}`} className={`mt-1 inline-block rounded px-2 py-0.5 text-[11px] ${block.cls}`}>{block.short} · achat indisponible</span>
              : bot.current_version && <p className="mt-0.5 text-xs text-white/35">Version {bot.current_version}</p>}
          </div>
        </div>

        <p className="mt-4 line-clamp-3 min-h-[60px] text-[13.5px] leading-relaxed text-white/55">
          {bot.description || PRODUCT_DEFAULTS[bot.id]?.description || "Découvrez ce que ce produit peut apporter à votre serveur."}
        </p>

        <div className="mt-5 flex items-end justify-between gap-3 border-t border-white/[0.06] pt-4">
          <div>
            {plan ? (
              <>
                <p className="text-[11px] uppercase tracking-wider text-white/35">À partir de</p>
                <p className="font-heading text-xl font-bold">{euros(Number(plan.price))}<span className="ml-1 text-xs font-normal text-white/35">/ {plan.duration_label || plan.name}</span></p>
              </>
            ) : (
              <p className="text-sm text-white/40">Prix sur la fiche</p>
            )}
          </div>
          <span
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white shadow-lg transition-all group-hover:gap-2.5"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)`, boxShadow: `0 10px 24px -10px ${color}` }}
          >
            Voir le produit
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </button>
  );
}

export default function ShopPage() {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [shopBots, setShopBots] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("all");
  const [reviews, setReviews] = useState({ count: 0, average: 0 });
  useEffect(() => {
    axios.get(`${API}/shop/bots`).then(r => setShopBots(r.data || [])).catch(() => {}).finally(() => setLoaded(true));
    axios.get(`${API}/reviews/stats`).then(r => setReviews(r.data || {})).catch(() => {});
  }, []);

  const counts = {
    all: shopBots.length,
    discord: shopBots.filter((b) => !b.downloadable).length,
    fivem: shopBots.filter((b) => b.downloadable).length,
  };
  const visible = shopBots.filter((b) => filter === "all" || (filter === "fivem" ? b.downloadable : !b.downloadable));

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar onAuthClick={() => setShowAuth(true)} />
      <main className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] overflow-hidden">
          <div className="absolute left-1/2 top-[-180px] h-[420px] w-[900px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/25 via-violet-600/20 to-cyan-500/15 blur-[90px]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-5 pb-20 pt-28 sm:px-6">
          <div className="shop-rise text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-blue-300" />Boutique officielle VantaPulse
            </span>
            <h1 className="mx-auto mt-5 max-w-3xl font-heading text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl">
              Des outils <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-violet-400 bg-clip-text text-transparent">pro</span> pour ta communauté
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-white/50">
              Bots Discord, scripts FiveM et plus encore. Choisis le produit qui correspond à ton serveur, on s'occupe du reste.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <button data-testid="shop-compare-link" onClick={() => navigate('/comparer')} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm text-white/85 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white">
                <Columns3 className="h-4 w-4" />Comparer les produits
              </button>
              {reviews.count > 0 && (
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/[0.06] px-4 py-2.5 text-sm text-white/50">
                  <span className="flex">{[1,2,3,4,5].map(n => <Star key={n} className={`h-4 w-4 ${reviews.average >= n ? 'fill-yellow-400 text-yellow-400' : 'text-white/10'}`} />)}</span>
                  {reviews.average}/5 · {reviews.count} avis
                </span>
              )}
            </div>
          </div>

          {/* Atouts */}
          <div className="shop-rise mt-12 grid grid-cols-2 gap-3 lg:grid-cols-4" style={{ animationDelay: "80ms" }}>
            {PERKS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 ring-1 ring-inset ring-white/10">
                  <Icon className="h-[18px] w-[18px] text-blue-200" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold">{title}</span>
                  <span className="block text-[11.5px] leading-snug text-white/40">{text}</span>
                </span>
              </div>
            ))}
          </div>

          {/* Filtres */}
          <div className="shop-rise mt-12 flex flex-wrap items-center justify-between gap-3" style={{ animationDelay: "140ms" }}>
            <h2 className="font-heading text-xl font-bold">Nos produits</h2>
            <div className="flex rounded-xl border border-white/[0.07] bg-white/[0.03] p-1" role="tablist">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  role="tab"
                  aria-selected={filter === f.id}
                  data-testid={`shop-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-all ${filter === f.id ? "bg-white/10 text-white shadow-sm" : "text-white/45 hover:text-white/80"}`}
                >
                  {f.label}<span className="ml-1.5 text-white/30">{counts[f.id]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {!loaded && [0, 1, 2].map((n) => (
              <div key={n} className="h-[400px] animate-pulse rounded-2xl border border-white/[0.05] bg-white/[0.02]" />
            ))}
            {visible.map((bot, index) => (
              <ShopCard key={`${filter}-${bot.id}`} bot={bot} index={index} onOpen={() => navigate(`/bot/${bot.id}`)} />
            ))}
          </div>

          {loaded && visible.length === 0 && (
            <div className="py-20 text-center">
              <Bot className="mx-auto mb-3 h-12 w-12 text-white/10" />
              <p className="text-white/30">Aucun produit en vente pour le moment</p>
            </div>
          )}

          {/* Aide au choix */}
          <div className="relative mt-16 overflow-hidden rounded-3xl border border-white/[0.07] bg-gradient-to-br from-blue-600/[0.12] via-transparent to-violet-600/[0.12] p-8 sm:p-10">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <h3 className="font-heading text-2xl font-bold">Besoin d'aide pour choisir ?</h3>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/50">Compare les produits côte à côte ou pose ta question à l'assistant IA en bas à droite de l'écran.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => navigate('/comparer')} className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#0a0a0f] transition hover:bg-white/90">
                  <Columns3 className="h-4 w-4" />Comparer
                </button>
                <button onClick={() => navigate('/support')} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-3 text-sm font-medium text-white/85 transition hover:bg-white/5">
                  <Headphones className="h-4 w-4" />Contacter le support
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
}
